// src/services/claimEventService.js
// Módulo 6 del spec del cliente (2026-09-17): "Event Schedule" — agenda de
// inspecciones/citas por reclamo (Initial Inspection, Re-inspection,
// Mediation, Appraisal/Umpire Inspection, EUO, Other). Solo el admin
// escribe (mismo criterio que Notes Log — el ajustador es de solo lectura
// salvo la excepción explícita de Attachments).
import { mergeById } from '../utils/claimAccess';
import { auth, db } from '../firebase/config';
import { getAssignedAdjusterEmail, syncZohoCalendarEvent } from './zohoCalendarService';
import { syncWorkspaceCalendarEvent } from './workspaceCalendarService';
import { DEFAULT_EVENT_TIMEZONE, eventTimezone } from './eventTimezones';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';

const EVENTS_COLLECTION = 'claimEvents';

export const EVENT_TYPES = [
  'initial_inspection',
  're_inspection',
  'mediation',
  'appraisal_inspection',
  'umpire_inspection',
  'euo',
  'recorded_statement',
  'other'
];

export const EVENT_TYPE_LABELS = {
  initial_inspection: { es: 'Inspección inicial', en: 'Initial Inspection' },
  re_inspection: { es: 'Re-inspección', en: 'Re-inspection' },
  mediation: { es: 'Mediación', en: 'Mediation' },
  appraisal_inspection: { es: 'Inspección de Appraisal', en: 'Appraisal Inspection' },
  umpire_inspection: { es: 'Inspección de Umpire', en: 'Umpire Inspection' },
  euo: { es: 'Examen Bajo Juramento (EUO)', en: 'Examination Under Oath (EUO)' },
  recorded_statement: { es: 'Declaración grabada (RS)', en: 'Recorded Statement (RS)' },
  other: { es: 'Otro', en: 'Other' }
};

const currentUserInfo = () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión.');
  return { email: user.email, name: user.displayName || user.email };
};

export const getClaimEvents = async (claimRecordId) => {
  const snapshot = await getDocs(
    query(collection(db, EVENTS_COLLECTION), where('claimRecordId', '==', claimRecordId))
  );
  return snapshot.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || ''));
};

// También usado para decidir visibilidad del calendario (ver
// getEventsForAdjuster más abajo): en Adjustify TODOS los ajustadores y
// asistentes de la empresa ven TODOS los eventos del calendario, no solo
// los suyos — a diferencia del resto de empresas, donde cada uno ve solo
// los propios salvo que tenga viewAllClaims. Pedido explícito 2026-09-19,
// "esto solo en una empresa".
export const usesZoho = (company = '') => ['adjustify', 'adjustify claims'].includes(company.trim().toLowerCase());

const syncEvent = async (eventId, event) => {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const claimSnapshot = await getDoc(doc(db, 'claimRecords', event.claimRecordId));
  const claim = claimSnapshot.exists() ? claimSnapshot.data() : null;
  if (!claim) {
    const result = { googleStatus: 'failed', zohoStatus: 'failed', syncError: 'No se encontró el reclamo.' };
    await updateDoc(eventRef, result);
    return result;
  }
  const company = claim.companyName || '';
  const zohoCompany = usesZoho(company);
  const metadata = { googleStatus: 'failed', zohoStatus: zohoCompany ? 'local_only' : 'not_applicable',
    syncError: '', googleError: '', zohoError: '', invitationError: '' };
  const errors = [];
  const attendeeEmail = await getAssignedAdjusterEmail(claim).catch(() => '');
  const title = `${EVENT_TYPE_LABELS[event.type]?.es || 'Cita'} · Reclamo #${claim.claimNumber || event.claimRecordId}`;
  const location = event.location || [claim.lossAddressStreet, claim.lossAddressCity, claim.lossAddressState, claim.lossAddressZip].filter(Boolean).join(', ') || claim.clientAddress || '';
  const common = { date: event.date, time: event.time || '', durationMinutes: event.durationMinutes || 60,
    timezone: eventTimezone(event), title, location, description: event.notes || '' };
  try {
    const result = await syncWorkspaceCalendarEvent({ ...common, action: event.googleEventId ? 'update' : 'create',
      sourceEventId: eventId, claimRecordId: event.claimRecordId, company,
      adjusterName: claim.adjusterName || '', attendeeEmail: zohoCompany ? '' : attendeeEmail });
    Object.assign(metadata, { googleStatus: 'synced', googleEventId: result.googleEventId, googleUrl: result.url || '' });
    if (!zohoCompany && !attendeeEmail) {
      metadata.invitationError = 'El ajustador no tiene un correo único y activo; la cita se guardó en Workspace sin invitación.';
    }
  } catch (error) { metadata.googleError = error.message; errors.push(`Google: ${error.message}`); }
  if (zohoCompany && event.time) {
    if (!attendeeEmail) {
      metadata.zohoStatus = 'failed';
      metadata.zohoError = 'El ajustador no tiene un correo único y activo en su perfil.';
      errors.push(`Zoho: ${metadata.zohoError}`);
    } else {
      try {
        const result = await syncZohoCalendarEvent({ ...common, action: event.zohoUid ? 'update' : 'create',
          company, zohoUid: event.zohoUid || '', attendeeEmail });
        Object.assign(metadata, { zohoStatus: 'synced', zohoUid: result.zohoUid, zohoUrl: result.url || '' });
      } catch (error) { metadata.zohoStatus = 'failed'; metadata.zohoError = error.message; errors.push(`Zoho: ${error.message}`); }
    }
  } else if (zohoCompany && event.zohoUid) {
    try {
      await syncZohoCalendarEvent({ action: 'delete', company, zohoUid: event.zohoUid });
      Object.assign(metadata, { zohoUid: '', zohoUrl: '', zohoStatus: 'local_only' });
    } catch (error) { metadata.zohoStatus = 'failed'; metadata.zohoError = error.message; errors.push(`Zoho: ${error.message}`); }
  }
  metadata.syncError = errors.join(' · ');
  await updateDoc(eventRef, metadata);
  return metadata;
};

export const retryClaimEventSync = async (event) => syncEvent(event.id, event);

export const createClaimEvent = async (claimRecordId, data) => {
  if (!data.date) throw new Error('La fecha del evento es obligatoria.');
  const { email, name } = currentUserInfo();
  // companyName/adjusterName/claimNumber/clientName se copian del claimRecord
  // al crear el evento (igual que checks/claimRecords) — así "Mi agenda"
  // (ClaimAgendaPage.jsx) puede consultar TODAS las citas de un ajustador
  // con un solo where('companyName','==',...) + where('adjusterName','==',...)
  // en vez de traer toda la colección claimEvents y filtrar en el cliente.
  const claimSnapshot = await getDoc(doc(db, 'claimRecords', claimRecordId));
  const claim = claimSnapshot.exists() ? claimSnapshot.data() : {};
  const payload = {
    claimRecordId,
    companyName: claim.companyName || '',
    adjusterName: claim.adjusterName || '',
    accessNames: claim.accessNames || [],
    claimNumber: claim.claimNumber || '',
    clientName: claim.clientName || '',
    type: EVENT_TYPES.includes(data.type) ? data.type : 'other',
    date: data.date,
    time: data.time || '',
    timezone: data.timezone || DEFAULT_EVENT_TIMEZONE,
    durationMinutes: Number(data.durationMinutes) || 60,
    confirmed: !!data.confirmed,
    notes: (data.notes || '').trim(),
    location: (data.location || '').trim(),
    createdByEmail: email,
    createdByName: name,
    createdAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, EVENTS_COLLECTION), payload);
  const sync = await syncEvent(docRef.id, payload);
  return { id: docRef.id, ...payload, ...sync };
};

// "Mi agenda" (ClaimAgendaPage.jsx en el portal /claim-status): todas las
// citas del ajustador a través de TODOS sus reclamos, no una por una. Un
// supervisor con viewAllClaims ve las de toda su empresa (mismo criterio
// que ClaimsDirectory/checks); un ajustador normal solo las suyas. El admin
// no tiene restricción de empresa — ve todo (isAdmin() ya lo permite en
// firestore.rules sin necesidad de where('companyName',...)).
export const getEventsForAdjuster = async ({ isAdmin, companyName, adjusterName, canViewAll }) => {
  const constraints = [];
  if (!isAdmin) {
    constraints.push(where('companyName', '==', companyName));
    // Solo el ajustador asignado ve su propio evento; viewAllClaims=true
    // (Luis Claudio, admin de la empresa Adjustify) es la única forma de
    // ver todos — pedido explícito 2026-09-19, corrige un intento previo
    // en sentido contrario en esta misma sesión.
    // Además de las suyas como PA principal, las de reclamos donde tiene
    // acceso por accessNames (utils/claimAccess.js).
    if (!canViewAll) {
      const base = collection(db, EVENTS_COLLECTION);
      const [own, shared] = await Promise.all([
        getDocs(query(base, ...constraints, where('adjusterName', '==', adjusterName))),
        getDocs(query(base, ...constraints, where('accessNames', 'array-contains', adjusterName)))
      ]);
      return sortEvents(mergeById([...own.docs, ...shared.docs].map(d => ({ id: d.id, ...d.data() }))));
    }
  }
  const snapshot = await getDocs(query(collection(db, EVENTS_COLLECTION), ...constraints));
  return sortEvents(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
};

const sortEvents = events => events
    .sort((a, b) => `${a.date || ''}T${a.time || ''}`.localeCompare(`${b.date || ''}T${b.time || ''}`));

export const updateClaimEvent = async (eventId, data) => {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const before = await getDoc(eventRef);
  if (!before.exists()) throw new Error('No se encontró la cita.');
  const previous = before.data();
  const payload = {
    type: EVENT_TYPES.includes(data.type) ? data.type : 'other',
    date: data.date,
    time: data.time || '',
    timezone: data.timezone || DEFAULT_EVENT_TIMEZONE,
    durationMinutes: Number(data.durationMinutes) || 60,
    confirmed: !!data.confirmed,
    notes: (data.notes || '').trim(),
    location: (data.location || '').trim()
  };
  await updateDoc(eventRef, payload);
  const changed = ['type', 'date', 'time', 'timezone', 'notes', 'location'].some(key => payload[key] !== previous[key])
    || payload.durationMinutes !== (previous.durationMinutes || 60);
  if (!changed) return { ...payload, zohoStatus: previous.zohoStatus };
  const sync = await syncEvent(eventId, { ...previous, ...payload });
  return { ...payload, ...sync };
};

export const deleteClaimEvent = async (eventId) => {
  const eventRef = doc(db, EVENTS_COLLECTION, eventId);
  const snapshot = await getDoc(eventRef);
  if (!snapshot.exists()) return;
  const event = snapshot.data();
  const errors = [];
  if (event.googleEventId) {
    try { await syncWorkspaceCalendarEvent({ action: 'delete', sourceEventId: eventId }); }
    catch (error) { errors.push(`Google: ${error.message}`); }
  }
  if (event.zohoUid) {
    const claimSnapshot = await getDoc(doc(db, 'claimRecords', event.claimRecordId));
    const company = claimSnapshot.data()?.companyName || '';
    try { await syncZohoCalendarEvent({ action: 'delete', company, zohoUid: event.zohoUid }); }
    catch (error) { errors.push(`Zoho: ${error.message}`); }
  }
  if (errors.length) {
    await updateDoc(eventRef, { syncError: errors.join(' · '), googleStatus: event.googleEventId ? 'failed' : event.googleStatus,
      zohoStatus: event.zohoUid ? 'failed' : event.zohoStatus });
    throw new Error(`No se pudo cancelar en todos los calendarios: ${errors.join(' · ')}`);
  }
  await deleteDoc(eventRef);
};

// Admin migration: copy legacy claimEvents into CSR Workspace without sending
// retroactive invitations. Existing Zoho events are left untouched.
export const backfillWorkspaceCalendar = async (onProgress = () => {}) => {
  currentUserInfo();
  const snapshot = await getDocs(collection(db, EVENTS_COLLECTION));
  const pending = snapshot.docs.filter(event => event.data().googleStatus !== 'synced');
  const result = { total: pending.length, synced: 0, failed: 0 };
  for (const eventDoc of pending) {
    const event = eventDoc.data();
    try {
      const claimSnapshot = await getDoc(doc(db, 'claimRecords', event.claimRecordId));
      if (!claimSnapshot.exists()) throw new Error('Reclamo no encontrado.');
      const claim = claimSnapshot.data();
      const location = event.location || [claim.lossAddressStreet, claim.lossAddressCity, claim.lossAddressState, claim.lossAddressZip].filter(Boolean).join(', ') || claim.clientAddress || '';
      const synced = await syncWorkspaceCalendarEvent({
        action: event.googleEventId ? 'update' : 'create', sourceEventId: eventDoc.id,
        claimRecordId: event.claimRecordId, company: claim.companyName || '',
        adjusterName: claim.adjusterName || '', attendeeEmail: '',
        title: `${EVENT_TYPE_LABELS[event.type]?.es || 'Cita'} · Reclamo #${claim.claimNumber || event.claimRecordId}`,
        date: event.date, time: event.time || '', durationMinutes: event.durationMinutes || 60,
        timezone: eventTimezone(event), location, description: event.notes || ''
      });
      await updateDoc(eventDoc.ref, { googleStatus: 'synced', googleEventId: synced.googleEventId,
        googleUrl: synced.url || '', googleError: '',
        // De paso, completa companyName/adjusterName/claimNumber/clientName
        // en eventos viejos creados antes de que existieran esos campos —
        // sin esto, "Mi agenda" (getEventsForAdjuster) nunca los encuentra.
        companyName: claim.companyName || '', adjusterName: claim.adjusterName || '',
        claimNumber: claim.claimNumber || '', clientName: claim.clientName || '' });
      result.synced += 1;
    } catch (error) {
      await updateDoc(eventDoc.ref, { googleStatus: 'failed', googleError: error.message });
      result.failed += 1;
    }
    onProgress({ ...result });
  }
  return result;
};
